import { LOAD_RESERVATION_OFFER } from "@/actions/LOAD_RESERVATION_OFFER.ts";
import { LOAD_RESERVATION_OFFER_RATE_PLAN } from "@/actions/LOAD_RESERVATION_OFFER_RATE_PLAN.ts";
import { useLazyGetAvailableRateGroups } from "@/actions/hooks/useLazyGetAvailableRateGroups.ts";
import { WarningToast } from "@/components/Notifications/WarningToast.tsx";
import { URLParam } from "@/helpers/interfaces/URLParam.ts";
import {
  LoadReservationOfferInput,
  LoadReservationOfferPayload,
  ReservationOfferAccommodation,
} from "@/helpers/types/types.ts";
import { useCalculateCart } from "@/helpers/useCalculateCart.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { AppPage } from "@/stores/EngineStore/enums/AppPage.ts";
import { useMutation } from "@apollo/client";
import { t } from "i18next";
import { useCallback, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useShallow } from "zustand/react/shallow";

export interface LROPayload {
  loadReservationOffer: LoadReservationOfferPayload;
}

export interface LROInput {
  loadReservationOfferInput: LoadReservationOfferInput;
}

export interface LRORatePlanPayload {
  loadReservationOffer: LoadReservationOfferPayload;
}

export interface LRORatePlanInput {
  loadReservationOfferInput: LoadReservationOfferInput;
  from: Date;
  until: Date;
}

export const useLoadReservationOffer = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const toastIdRef = useRef<string | null>(null);

  const { resetCartItems, setFullCartAccommodationItems, setPage } = useEngineStore(
    useShallow((state) => ({
      resetCartItems: state.resetCartItems,
      setFullCartAccommodationItems: state.setFullCartAccommodationItems,
      setPage: state.setPage,
    })),
  );

  const { handleCalculateCart } = useCalculateCart();
  const { handleFetchAvailableRGs } = useLazyGetAvailableRateGroups();

  const [loadReservationOfferMutation] = useMutation<LROPayload, LROInput>(LOAD_RESERVATION_OFFER, {
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });

  const [loadReservationOfferRatePlanMutation] = useMutation<LRORatePlanPayload, LRORatePlanInput>(
    LOAD_RESERVATION_OFFER_RATE_PLAN,
    {
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
    },
  );

  const mergeAccommodationData = useCallback(
    (
      baseAccommodations: ReservationOfferAccommodation[],
      ratePlanAccommodations: LRORatePlanPayload["loadReservationOffer"]["reservationOffer"]["accommodations"],
    ): ReservationOfferAccommodation[] => {
      return baseAccommodations.map((baseAccom) => {
        const ratePlanAccom = ratePlanAccommodations.find((rpAccom) => rpAccom.id === baseAccom.id);

        if (!ratePlanAccom) {
          console.warn(`No rate plan data found for accommodation ${baseAccom.id}`);
          return baseAccom;
        }

        return {
          ...baseAccom,
          ratePlan: {
            ...baseAccom.ratePlan,
            ...ratePlanAccom.ratePlan,
          },
        };
      });
    },
    [],
  );

  const handleOnCompleted = useCallback(
    (mergedAccommodations: ReservationOfferAccommodation[]) => {
      if (mergedAccommodations && mergedAccommodations.length > 0) {
        setFullCartAccommodationItems(mergedAccommodations);
      } else {
        resetCartItems();
      }
    },
    [resetCartItems, setFullCartAccommodationItems],
  );

  const handleRemoveReservationOfferURLParam = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete(URLParam.ReservationOffer);
    window.history.replaceState({}, "", `${window.location.pathname}?${urlParams.toString()}`);
  }, []);

  const handleLoadReservationOfferWithRatePlan = useCallback(
    async (reservationOfferId: string) => {
      try {
        setIsLoading(true);
        setError(null);

        // Load basic reservation offer data first to get dates
        const baseResponse = await loadReservationOfferMutation({
          variables: { loadReservationOfferInput: { reservationOfferId } },
        });

        const reservationOffer = baseResponse.data?.loadReservationOffer.reservationOffer;

        // Check if the mutation actually succeeded and has data
        if (!reservationOffer?.accommodations?.length) {
          console.warn("No accommodations found in reservation offer");
          resetCartItems();
          return;
        }

        // Get date range from first accommodation for detailed rate plan data
        const firstAccommodation = reservationOffer.accommodations[0];
        const from = new Date(firstAccommodation.checkIn);
        const until = new Date(firstAccommodation.checkOut);

        // Fetch available rate groups needed for cart conversion
        await handleFetchAvailableRGs();

        // Load detailed rate plan data with the dates
        const ratePlanResponse = await loadReservationOfferRatePlanMutation({
          variables: {
            loadReservationOfferInput: { reservationOfferId },
            from,
            until,
          },
        });

        const ratePlanAccommodations =
          ratePlanResponse.data?.loadReservationOffer.reservationOffer.accommodations;

        if (!ratePlanAccommodations) {
          console.warn("Failed to load rate plan data");
          handleOnCompleted(reservationOffer.accommodations);
          return;
        }

        // Merge the basic and detailed data
        const mergedAccommodations = mergeAccommodationData(
          reservationOffer.accommodations,
          ratePlanAccommodations,
        );

        // Update the store with merged data and navigate to ExtraOptions
        handleOnCompleted(mergedAccommodations);
        setPage(AppPage.ExtraOptions);

        // Wait for store to update, then calculate cart with the loaded reservation data
        void handleCalculateCart();

        // Wait for extra options & calculate loadings to complete
        await new Promise((resolve) => setTimeout(resolve, 1000));
      } catch (error) {
        console.error("Error loading reservation offer:", error);

        toastIdRef.current = toast.custom(<WarningToast message={t("offerNotValidAnymore")} />, {
          id: toastIdRef?.current || undefined,
        });
        setError(error as Error);
        resetCartItems();
        handleRemoveReservationOfferURLParam();

        setPage(AppPage.AccommodationPicking);
      } finally {
        setIsLoading(false);
      }
    },
    [
      loadReservationOfferMutation,
      loadReservationOfferRatePlanMutation,
      mergeAccommodationData,
      handleOnCompleted,
      resetCartItems,
      setPage,
      handleRemoveReservationOfferURLParam,
      handleCalculateCart,
      handleFetchAvailableRGs,
    ],
  );

  return {
    handleLoadReservationOffer: handleLoadReservationOfferWithRatePlan,
    reservationOfferLoading: isLoading,
    loadReservationOfferError: error,
  };
};
